import './ChangePlan.css';

import { CheckCircleOutlined, InfoCircleOutlined, LeftOutlined } from '@ant-design/icons';
import { Spin, Tooltip } from 'antd';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

import GlobirdSelector from 'components/Shared/GlobirdSelector';
import powerIcon from 'assets/power-icon.svg';
import gasIcon from 'assets/gas-icon.svg';
import { ChangePlanOffer, ChangePlanOfferList, ChangePlanQuoteResponse, StateCode } from 'data/CustomerPortalTypes';
import { getChangePlanOffers, sendChangePlanQuote } from 'services/RequestDataService';
import { selectCurrentAccount } from 'store/userSlice';
import { delay, showErrorToast } from 'utilities/Utility';
import { useSupportMenuOptions } from './MenuOptions';

const eligibilityCriteria = 'Eligibility criteria: This plan is only available to new customers or existing customers at a new property with the same distribution area and network tariff.';
const quoteRequestTimeoutMs = 30000;
const actionStatusText: Record<ChangePlanOffer['actionStatus'], string> = {
    Available: 'Send Quote',
    AwaitingAcceptance: 'Awaiting Acceptance',
    OfferAccepted: 'Offer Accepted',
    RequestReceived: 'Request Received',
    PlanUnavailable: 'Plan Unavailable'
};

const ChangePlan = () => {
    const navigate = useNavigate();
    const currentAccount = useSelector(selectCurrentAccount);
    const menuOptions = useSupportMenuOptions();
    const [offerList, setOfferList] = useState<ChangePlanOfferList | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasOfferError, setHasOfferError] = useState(false);
    const [submittingServiceId, setSubmittingServiceId] = useState<number | null>(null);
    const [confirmation, setConfirmation] = useState<ChangePlanQuoteResponse | null>(null);
    const accountId = currentAccount?.accountId;
    const hasVicService = currentAccount?.services.some(service => service.state === StateCode.VIC) ?? false;
    const requestContext = useRef({ accountId, active: false, offerRequest: 0, quoteRequest: 0 });

    useLayoutEffect(() => {
        // Each account visit gets a new context, including A -> B -> A.
        // Invalidate before paint so old responses cannot update the next account's UI.
        const context = { accountId, active: true, offerRequest: 0, quoteRequest: 0 };
        requestContext.current = context;
        setOfferList(null);
        setHasOfferError(false);
        setConfirmation(null);
        setSubmittingServiceId(null);
        setLoading(!!accountId && hasVicService);

        return () => { context.active = false; };
    }, [accountId, hasVicService]);

    const loadOffers = useCallback(async () => {
        const context = requestContext.current;
        if (!accountId || !hasVicService || !context.active || context.accountId !== accountId) {
            return;
        }

        const requestId = ++context.offerRequest;
        const isCurrentRequest = () => context.active
            && requestContext.current === context
            && context.offerRequest === requestId;
        setLoading(true);
        setHasOfferError(false);
        try {
            const response = await getChangePlanOffers(accountId);
            if (!isCurrentRequest()) {
                return;
            }

            if (response?.success && response.data && Array.isArray(response.data.offers)) {
                setOfferList(response.data);
            } else {
                setOfferList(null);
                setHasOfferError(true);
            }
        } catch {
            if (isCurrentRequest()) {
                setOfferList(null);
                setHasOfferError(true);
            }
        } finally {
            if (isCurrentRequest()) {
                setLoading(false);
            }
        }
    }, [accountId, hasVicService]);

    useEffect(() => {
        void loadOffers();
    }, [loadOffers]);

    if (!currentAccount || !hasVicService) {
        return <Navigate to="/dashboard" replace />;
    }

    const requestQuote = async (offer: ChangePlanOffer) => {
        const context = requestContext.current;
        if (!context.active || context.accountId !== accountId) {
            return;
        }

        const requestId = ++context.quoteRequest;
        const isCurrentRequest = () => context.active
            && requestContext.current === context
            && context.quoteRequest === requestId;
        setSubmittingServiceId(offer.accountServiceId);
        try {
            const result = await Promise.race([
                sendChangePlanQuote(offer.accountServiceId)
                    .then(response => ({ timedOut: false as const, response })),
                delay(quoteRequestTimeoutMs)
                    .then(() => ({ timedOut: true as const }))
            ]);
            if (!isCurrentRequest()) {
                return;
            }

            if (result.timedOut) {
                showErrorToast(
                    'Your request is taking longer than expected. Please refresh the page to check its status before trying again.');
                return;
            }

            if (result.response?.success && result.response.data?.emailAddress) {
                setConfirmation(result.response.data);
            } else {
                showErrorToast(result.response?.message || 'Unable to request the quote. Please try again.');
            }
        } finally {
            if (isCurrentRequest()) {
                setSubmittingServiceId(null);
            }
        }
    };

    const done = async () => {
        setConfirmation(null);
        await loadOffers();
    };

    const selectedOption = menuOptions.find(option => option.value === 'changeplan');

    return (
        <div className="change-plan-page">
            <div className="globird-menu-option">
                <p className="globird-menu-label">Show me: </p>
                <GlobirdSelector
                    id="supportMenuSelector"
                    options={menuOptions}
                    defaultOption={selectedOption}
                    onSelected={option => navigate(`/${option.value}`)} />
            </div>

            <Spin spinning={loading} delay={300}>
                {loading
                    ? <section className="change-plan-card change-plan-loading" aria-busy="true" aria-label="Loading plans" />
                    : confirmation
                    ? <QuoteRequested confirmation={confirmation} onDone={done} />
                    : hasOfferError || !offerList
                    ? <OfferLoadError onRetry={loadOffers} />
                    : <OfferContent
                        offers={offerList.offers}
                        submittingServiceId={submittingServiceId}
                        onRequestQuote={requestQuote} />}
            </Spin>

            {!confirmation &&
                <button className="change-plan-back" type="button" onClick={() => navigate('/dashboard')}>
                    <LeftOutlined aria-hidden="true" /> Back
                </button>}
        </div>
    );
};

const OfferLoadError = (props: { onRetry: () => void }) => (
    <section className="change-plan-card change-plan-empty change-plan-error" role="alert">
        <h2>We couldn&rsquo;t load your offers right now.</h2>
        <p>Please try again. If the problem continues, contact our Customer Service team.</p>
        <button className="globird-button-primary change-plan-retry" type="button" onClick={props.onRetry}>Retry</button>
    </section>
);

const OfferContent = (props: {
    offers: ChangePlanOffer[],
    submittingServiceId: number | null,
    onRequestQuote: (offer: ChangePlanOffer) => void
}) => {
    if (props.offers.length === 0) {
        return (
            <section className="change-plan-card change-plan-empty">
                <h2>There&rsquo;s no better offer for this account right now.</h2>
                <p>We haven&rsquo;t identified a better offer from your recent bill, or a previously identified offer may have expired.</p>
            </section>
        );
    }

    return (
        <section className="change-plan-card">
            <div className="change-plan-intro">
                <h2>Seen a better offer on your recent bill and want to switch?</h2>
                <p>Select <strong>Send quote</strong> and we&rsquo;ll email you the offer details, including energy rates, and instructions on how to switch.</p>
                <h3>Please note:</h3>
                <ul>
                    <li>Offers shown on your bill will appear here for a limited time until they expire, and further eligibility criteria apply.</li>
                    <li>If the new plan has the same name as your current plan, you are on an older version of the plan which has different rates. You can still save money by switching to a newer version.</li>
                </ul>
            </div>

            <div className="change-plan-table-wrap">
                <table className="change-plan-table" aria-label="Available best offers">
                    <thead>
                        <tr>
                            <th>NMI/MIRN</th>
                            <th>Fuel Type</th>
                            <th>Address</th>
                            <th>Current Plan</th>
                            <th>New Plan</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.offers.map(offer => {
                            const isAvailable = offer.actionStatus === 'Available';
                            const submitting = props.submittingServiceId === offer.accountServiceId;
                            return (
                                <tr key={offer.accountServiceId}>
                                    <td data-label="NMI/MIRN">{offer.siteIdentifier}</td>
                                    <td data-label="Fuel Type">
                                        <span className="change-plan-fuel">
                                            <img src={offer.fuelType === 'Gas' ? gasIcon : powerIcon} alt="" />
                                            {offer.fuelType}
                                        </span>
                                    </td>
                                    <td data-label="Address">{offer.address}</td>
                                    <td data-label="Current Plan">{offer.currentPlan}</td>
                                    <td data-label="New Plan">
                                        <span className="change-plan-new-plan">
                                            <span>{offer.newPlan}</span>
                                            {offer.showEligibilityCriteria &&
                                                <Tooltip title={eligibilityCriteria} color="#2e2e2d" overlayInnerStyle={{ borderRadius: '10px' }}>
                                                    <button className="change-plan-info" type="button" aria-label={eligibilityCriteria}>
                                                        <InfoCircleOutlined />
                                                    </button>
                                                </Tooltip>}
                                        </span>
                                    </td>
                                    <td data-label="Action">
                                        <button
                                            className={`globird-button-primary change-plan-action${!isAvailable ? ' change-plan-awaiting' : ''}`}
                                            type="button"
                                            aria-busy={submitting}
                                            disabled={!isAvailable || submitting || props.submittingServiceId !== null}
                                            onClick={() => props.onRequestQuote(offer)}>
                                            {submitting ? 'Submitting...' : actionStatusText[offer.actionStatus] || 'Unavailable'}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

const QuoteRequested = (props: { confirmation: ChangePlanQuoteResponse, onDone: () => void }) => (
    <section className="change-plan-card change-plan-confirmation" aria-live="polite">
        <h2><CheckCircleOutlined /> Quote requested</h2>
        <p>We&rsquo;ll email a quote to <strong>{props.confirmation.emailAddress}</strong> in 1-2 business days.</p>
        <p>Please follow the instructions in the email to review and accept the offer. Nothing will change on your account until you accept it.</p>
        <button className="globird-button-primary change-plan-done" type="button" onClick={props.onDone}>Done</button>
    </section>
);

export default ChangePlan;
